import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MuiI18nProvider } from '../i18n';
import { Observable } from 'rxjs';

export interface PageChangeEvent {
  currentPageIndex: number;
  previousPageIndex: number;
  size: number;
  total: number;
}

@Component({
  selector: 'mui-pagination',
  templateUrl: './mui-pagination.component.html',
  styleUrls: ['./mui-pagination.component.scss'],
  exportAs: 'muiPagination'
})
export class MuiPagination {
  @Input()
  size = 25;

  @Input()
  sizeOptions = [5, 10, 25, 50];

  @Input()
  currentPageIndex = 0;

  @Input()
  total = 0;

  @Input()
  sizeSelectLabel?: string;

  @Input()
  displayedItemsLabel?: string;

  @Input()
  previousPageButtonLabel?: string;

  @Input()
  nextPageButtonLabel?: string;

  get totalPages(): number {
    return Math.ceil(this.total / this.size);
  }

  get from(): number {
    return this.currentPageIndex * this.size + 1;
  }

  get to(): number {
    return Math.min((this.currentPageIndex + 1) * this.size, this.total);
  }

  get isFirstPage(): boolean {
    return this.currentPageIndex <= 0;
  }

  get isLastPage(): boolean {
    return this.currentPageIndex >= (this.totalPages - 1);
  }

  @Output()
  protected pageChange = new EventEmitter<PageChangeEvent>();

  protected get translations$(): Observable<{
    displayedItemsLabel: string,
    nextPageButton: string,
    previousPageButton: string,
    sizeSelectLabel: string
  }> {
    return this._i18n.pagination(this.from, this.to, this.total);
  }

  constructor(private _i18n: MuiI18nProvider) {}

  changeSize(size: number): void {
    // Reset the current page to 1 when changing the size
    this.currentPageIndex = 0;
    this.size = size;

    this.pageChange.emit({
      size,
      currentPageIndex: this.currentPageIndex,
      total: this.total,
      previousPageIndex: this.currentPageIndex,
    });
  }

  nextPage(): void {
    if (this.isLastPage) {
      return;
    }

    this.currentPageIndex++;
    this.pageChange.emit({
      size: this.size,
      currentPageIndex: this.currentPageIndex,
      total: this.total,
      previousPageIndex: this.currentPageIndex - 1,
    });
  }

  previousPage(): void {
    if (this.isFirstPage) {
      return;
    }

    this.currentPageIndex--;
    this.pageChange.emit({
      size: this.size,
      currentPageIndex: this.currentPageIndex,
      total: this.total,
      previousPageIndex: this.currentPageIndex + 1,
    });
  }
}
